import { NavigableButton } from '@carvana/ds-button';
import Link from '@carvana/ds-link';
import { Clock, MapPin, Phone } from 'lucide-react';
import { useMemo } from 'react';
import GoogleMap from '~/components/dealership/GoogleMap';
import ShiftDigitalEventName from '~/constants/dealership/ShiftDigitalEventName';
import { useIsMobile } from '~/context/common/DeviceContext';
import {
  useDealershipConfiguration,
  useDealershipDepartmentHours,
  useDealershipServiceAddress
} from '~/context/dealership/StaticContext';
import useServicePhone from '~/hooks/dealership/selectors/useServicePhone';
import useGoogleMapsDirection from '~/hooks/dealership/useGoogleMapsDirection';

export default function FindServiceCenter() {
  const isMobile = useIsMobile();
  const address = useDealershipServiceAddress();
  const servicePhone = useServicePhone();
  const hours = useDealershipDepartmentHours();
  const metadata = useDealershipConfiguration();
  const googleMapsDirections = useGoogleMapsDirection(address);
  const serviceHours = hours.find(
    h => h.label?.toLowerCase().includes('service')
  );

  const mapProps = useMemo(() => {
    const offset = isMobile ? 0.1 : 0.009;
    const newPosition = { lat: 0, lng: 0, ...address.position };

    if (isMobile) {
      newPosition.lat -= offset;
    } else {
      newPosition.lng -= offset;
    }

    return { center: newPosition, zoom: isMobile ? 11 : 14.5 };
  }, [address.position, isMobile]);

  return (
    <section className="bg-canvas-default px-lg pb-4xl md:px-2xl md:pb-6xl lg:px-4xl">
      <div className="mx-auto max-w-[1272px] isolate overflow-hidden rounded-2xl border border-border-subtle">
        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr]">
          <div className="relative h-[250px] md:h-[300px] lg:h-auto lg:min-h-[460px]">
            <GoogleMap {...mapProps} />
          </div>

          <div className="flex flex-col justify-center gap-y-2xl p-2xl md:p-3xl lg:p-4xl">
            <span className="text-text-primary t-label-xs tracking-widest">
              FIND OUR SERVICE CENTER
            </span>

            <h2 className="text-text-strong t-heading-2xl">
              {metadata.name ?? 'Carvana'} Service Center
            </h2>

            <div className="flex flex-col gap-y-lg">
              <div className="flex items-start gap-x-lg">
                <div className="flex size-[36px] shrink-0 items-center justify-center rounded-[10px] bg-background-primary-muted">
                  <MapPin className="size-[18px] text-text-primary" />
                </div>
                <div className="text-text-strong t-body-md">
                  <div>{address.street1}</div>
                  <div>
                    {address.city}, {address.state} {address.zip}
                  </div>
                </div>
              </div>

              {serviceHours && (
                <div className="flex items-start gap-x-lg">
                  <div className="flex size-[36px] shrink-0 items-center justify-center rounded-[10px] bg-background-primary-muted">
                    <Clock className="size-[18px] text-text-primary" />
                  </div>
                  <div className="text-text-strong t-body-md">
                    {serviceHours.daysHours?.map(entry => (
                      <div key={entry.days}>
                        {entry.days} {entry.hours}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {servicePhone && (
                <div className="flex items-start gap-x-lg">
                  <div className="flex size-[36px] shrink-0 items-center justify-center rounded-[10px] bg-background-primary-muted">
                    <Phone className="size-[18px] text-text-primary" />
                  </div>
                  <div className="t-body-md">
                    <div>
                      <span className="text-text-default">Service: </span>
                      <Link
                        href={`tel:${servicePhone.number}`}
                        size="sm"
                        preventVisitedColor
                        data-adobe-id="click-to-call-service"
                        data-analytics-id="Service Find Center - Phone"
                        data-sda-event={ShiftDigitalEventName.ClickToCall}
                        data-sda-event-set-prop="clickToCallDepartment|Service"
                        data-testid="find-center-phone-service"
                      >
                        {servicePhone.number}
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-lg sm:flex-row sm:flex-wrap">
              <NavigableButton
                variant="primary"
                size="md"
                width="fit-content"
                href={googleMapsDirections}
                target="_blank"
                rel="noopener noreferrer"
                data-adobe-id="get-directions-service"
                data-analytics-id="Service Find Center - Get Directions"
                data-sda-event={ShiftDigitalEventName.GetDirections}
                data-testid="service-get-directions"
              >
                Get directions
              </NavigableButton>

              <NavigableButton
                variant="secondary"
                size="md"
                width="fit-content"
                href="/schedule"
                data-adobe-id="schedule-service-location"
                data-analytics-id="Service Find Center - Schedule Service"
                data-sda-event={ShiftDigitalEventName.ScheduleServiceClick}
                data-testid="service-location-schedule"
              >
                Schedule service
              </NavigableButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
